import config from '../config.js';
import { authFetch } from '../utils/apiClient.js';

// filesByType: { [documentType]: File | File[] }. With categorise=true this
// hits the DOCUMENT_CATEGORISE workflow and triggers a single batch
// extraction job.
async function uploadCase(clientId, filesByType, { categorise = false } = {}) {
  const formData = new FormData();
  Object.entries(filesByType).forEach(([documentType, files]) => {
    (Array.isArray(files) ? files : [files]).forEach((file) => formData.append(documentType, file));
  });
  const url = `${config.backendUrl}/v2/cases/upload/${clientId}${categorise ? '?categorise=true' : ''}`;
  const res = await authFetch(url, { method: 'POST', body: formData });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail?.message || detail?.detail || `Upload failed (${res.status})`);
  }
  return res.json();
}

async function getCase(clientId, caseId) {
  const res = await authFetch(`${config.backendUrl}/v2/cases/${clientId}/case/${caseId}`);
  if (!res.ok) throw new Error(`Failed to load case (${res.status})`);
  return res.json();
}

async function addDocument(clientId, caseId, documentType, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/${documentType}`,
    { method: 'POST', body: formData }
  );
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.detail?.message || `Upload failed (${res.status})`);
  }
  return res.json();
}

async function deleteDocument(clientId, caseId, documentId) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/${documentId}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(`Failed to delete document (${res.status})`);
  return res.json();
}

async function setDocumentCategory(clientId, caseId, update) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/category`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    }
  );
  if (!res.ok) throw new Error(`Failed to update category (${res.status})`);
  return res.json();
}

async function listCompleteCases(clientId) {
  const res = await authFetch(`${config.backendUrl}/v2/cases/${clientId}/complete`);
  if (!res.ok) throw new Error(`Failed to load cases (${res.status})`);
  return res.json();
}

async function resolveDocumentCategory(clientId, caseId, documentId, categoryCaseworker) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/category/resolve`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: documentId, category_caseworker: categoryCaseworker }),
    }
  );
  if (!res.ok) throw new Error(`Failed to resolve category (${res.status})`);
  return res.json();
}

async function approveDocumentCategory(clientId, caseId, documentId) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/category/approve`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_id: documentId }),
    }
  );
  if (!res.ok) throw new Error(`Failed to approve category (${res.status})`);
  return res.json();
}

// Manually restarts categorisation/extraction over the case's current
// document set - needed after addDocument/deleteDocument since those don't
// retrigger it themselves (FIQ3-B01 follow-up).
async function reextractCase(clientId, caseId) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/reextract`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(`Neuauswertung konnte nicht gestartet werden (${res.status})`);
  return res.json();
}

async function getDocumentHistory(clientId, caseId, documentId) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/${documentId}/history`
  );
  if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
  return res.json();
}

async function getDocumentPreviewUrl(clientId, caseId, documentId) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}/${documentId}/preview`
  );
  if (!res.ok) throw new Error(`Vorschau konnte nicht geladen werden (${res.status})`);
  return res.json();
}

// Persists the (possibly citizen-corrected) document_extractions and the
// citizen's personal details/legal consent (applicant), marks the case
// COMPLETE, and triggers DOCUMENT_INDEX - see routes/v2/case.py's
// submit_case_data (shared with the older DOCUMENT_PROCESS flow via the
// `categorise` query flag).
async function submitCase(clientId, caseId, documentExtractions = [], applicant = null) {
  const res = await authFetch(
    `${config.backendUrl}/v2/cases/${clientId}/case/${caseId}?categorise=true`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_extractions: documentExtractions, applicant }),
    }
  );
  if (!res.ok) throw new Error(`Antrag konnte nicht abgesendet werden (${res.status})`);
  return res.json();
}

export const caseApi = {
  uploadCase, getCase, addDocument, deleteDocument, setDocumentCategory,
  listCompleteCases, resolveDocumentCategory, approveDocumentCategory, getDocumentHistory, getDocumentPreviewUrl,
  submitCase, reextractCase,
};
